import {AppViewModel} from './app.view-model';
import {PlatformService} from './presentation/services/platform.service';
import {DOCUMENT, registerLocaleData} from "@angular/common";
import localeES from '@angular/common/locales/es';
import localePT from '@angular/common/locales/pt';
import localeFR from '@angular/common/locales/fr';
import localeEN from '@angular/common/locales/en';
import {Component, Inject, NgZone, OnDestroy, OnInit} from "@angular/core";
import {DisplayService} from "@app/presentation/services/display.service";
import {FirebaseCrashlytics} from '@capacitor-firebase/crashlytics';
import {Keyboard} from "@capacitor/keyboard";
import {TranslateService} from "@ngx-translate/core";
import {environment} from "src/environments/environment";
import {SideSheetService} from "@app/presentation/services/side-sheet/side-sheet.service";
import {SeoService} from "@app/presentation/services/seo/seo.service";
import {LocalizedRouter} from "@app/presentation/services/localized-router";
import {LocalizeRouterService} from "@gilsdav/ngx-translate-router";
import {Capacitor, CapacitorCookies} from "@capacitor/core";
import {Subject} from "rxjs";
import {NavigationAction} from "@app/core/shared/domain/entities/navigation-action";
import {LoadingPopupService} from "@app/presentation/services/loading/loading-popup.service";
import {NavigationActionToRouteMapper} from "@app/presentation/utils/navigation-action-to-route.mapper";
import {SplashScreen} from '@capacitor/splash-screen';
import {InitializeAppUseCase} from "@app/core/shared/application/initialize-app.use-case";
import {RealUserMonitoringService, RUMContextKey} from './presentation/services/real-user-monitoring.service';
import {UserTrackingService} from "@app/core/user/domain/user-tacking.service";
import {AppTrackingTransparencyService} from "@app/presentation/services/app-tracking-transparency.service";
import {CookieBannerService} from "@app/presentation/services/cookie-banner.service";
import {MigrationService} from '@app/core/shared/infrastructure/migration/migration.service';
import {UserIdleDetectorService} from "@app/presentation/services/user-idle-detector-service";
import {DownloadAppModalOpenerService} from "@app/presentation/services/download-app-modal/download-app-modal-opener-service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  providers: [AppViewModel],
  // This is needed to avoid hydration errors in all app.
  host: {ngSkipHydration: 'true'},
})
export class AppComponent implements OnInit, OnDestroy {
  title = "europastry";
  isMobile$ = this.displayService.isMobileDisplay$

  sideSheetContent$ = this.sideSheetService.currentComponentPortal$;

  isSideSheetOpen$ = this.sideSheetService.isSideSheetOpen$;

  private destroySubject: Subject<boolean> = new Subject<boolean>();

  private readonly SPLASH_SCREEN_DELAY = 1500;
  private readonly SPLASH_SCREEN_FADE_OUT_DURATION = 200;

  constructor(
    private translate: TranslateService,
    private displayService: DisplayService,
    private zone: NgZone,
    private sideSheetService: SideSheetService,
    private seoService: SeoService,
    private localizedRouter: LocalizedRouter,
    private platformService: PlatformService,
    @Inject(DOCUMENT) private document: Document,
    private localizeRouterService: LocalizeRouterService,
    private viewModel: AppViewModel,
    private loadingService: LoadingPopupService,
    private navigationActionToRouteMapper: NavigationActionToRouteMapper,
    private initializeAppUseCase: InitializeAppUseCase,
    private realUserMonitoringService: RealUserMonitoringService,
    private appTrackingTransparencyService: AppTrackingTransparencyService,
    private userTrackingService: UserTrackingService,
    private cookieBannerService: CookieBannerService,
    private migrationService: MigrationService,
    private userIdleDetectorService: UserIdleDetectorService,
    private downloadAppModalOpenerService: DownloadAppModalOpenerService,
  ) {
    this.viewModel.init();
    this.initializeApp();
  }

  ngOnInit() {
    //Set the default title for all pages
    this.seoService.setTitle(this.translate.instant('seo.default_title'));
    this.closeSplashScreen();
  }

  private closeSplashScreen() {
    setTimeout(() => {
      SplashScreen.hide({
        fadeOutDuration: this.SPLASH_SCREEN_FADE_OUT_DURATION,
      });
    },
      // The splash screen is shown for SPLASH_SCREEN_DELAY seconds after the app component is initialized.
      this.SPLASH_SCREEN_DELAY
    );
  }

  ngOnDestroy() {
    this.destroySubject.next(true);
    this.destroySubject.unsubscribe();
    this.viewModel.destroy();
  }

  async initializeApp() {
    await this.migrationService.executeMigrations();
    if (Capacitor.getPlatform() === 'ios') {
      await this.initializeForIOS();
    }
    this.realUserMonitoringService.init();
    this.realUserMonitoringService.setContext(RUMContextKey.PLATFORM, environment.PLATFORM);
    this.configLocale();
    this.listenDeepLinksNavigationAction();
    this.initializeOnLanguageChangeSubscription();

    if (Capacitor.isNativePlatform()) {
      this.configFirebaseCrashlytics();
      Keyboard.setAccessoryBarVisible({ isVisible: true });
    }
    if (this.platformService.isBrowser) {
      await this.initializeAppUseCase.execute();
      this.userIdleDetectorService.start();
      this.listenDisplayDownloadAppModalEvents();
    }

  }

  async initializeForIOS() {
    this.cookieBannerService.hide();
    const isTrackingPermissionGranted = await this.appTrackingTransparencyService.requestTrackingPermission();
    if (isTrackingPermissionGranted) {
      this.cookieBannerService.acceptAll();
    } else {
      await CapacitorCookies.clearAllCookies();
      this.cookieBannerService.acceptNecessary();
    }
    this.userTrackingService.registerPushService();
  }

  /**
   * Updates <html lang="es">
   */
  private initializeOnLanguageChangeSubscription() {
    this.document.documentElement.lang = this.localizeRouterService.parser.currentLang;
    this.translate.onLangChange.subscribe({
      next: () => this.document.documentElement.lang = this.localizeRouterService.parser.currentLang
    });
  }

  private configFirebaseCrashlytics() {
    if (environment.isDevelopment) {
      this.enableFirebaseCrashlytics(false);
    } else {
      this.enableFirebaseCrashlytics(true);
    }
  }

  private enableFirebaseCrashlytics(enable: boolean) {
    FirebaseCrashlytics.setEnabled({
      enabled: enable
    })
  }

  private configLocale() {
    registerLocaleData(localePT);
    registerLocaleData(localeES);
    registerLocaleData(localeFR);
    registerLocaleData(localeEN);
  }

  /**
   * On iOS devices when opening the virtual keyboard the activeElement may get hidden behind the keyboard
   * Scroll into the active element before opening the keyboard
   */
  async keyboardResize(): Promise<void> {
    Keyboard.addListener("keyboardWillShow", () => {
      requestAnimationFrame(() => {
        document.activeElement?.scrollIntoView({ block: "center" })
      });
    });
  }

  /**
   * Listen the deep links state and navigation action.
   */
  listenDeepLinksNavigationAction() {
    //If is rendered in server side, do nothing
    if (this.platformService.isServer) {
      return;
    }
    this.listenNavigationAction();
    this.listenDeepLinkLoadingState();
  }

  /**
   * Listen the deep links loading state and show or hide the loading spinner popup.
   */
  listenDeepLinkLoadingState() {
    this.viewModel.isDeepLinkLoading$.subscribe({
        next: isDeepLinkLoading => {
          // ngZone.run is needed because the event is triggered outside the angular zone from a native plugin.
          this.zone.run(() => {
            if (isDeepLinkLoading) {
              this.loadingService.show();
            } else {
              this.loadingService.hide();
            }
          });
        }
    });
  }

  listenNavigationAction() {
    this.viewModel.navigationAction$.subscribe({
      next: navigationAction => {
        // ngZone.run is needed because the navigation action is triggered from a native plugin.
        this.zone.run(() => {
          this.handleNavigationAction(navigationAction);
        });
      }
    });
  }

  /**
   * Listens for events to display the "Download App" modal and triggers its opening.
   */
  listenDisplayDownloadAppModalEvents() {
    this.viewModel.displayDownloadAppModalEvents$.subscribe({
      next: () => {
        this.downloadAppModalOpenerService.open();
      }
    });
  }

  /**
   * Handle the navigation event and navigate to the correct route.
   * @param navigationAction
   */
  handleNavigationAction(navigationAction: NavigationAction) {
    const route = this.navigationActionToRouteMapper.map(navigationAction);
    if (route == null) {
      return;
    }
    this.localizedRouter.navigateByUrl(route.toString());
  }

  closeSideSheet() {
    this.sideSheetService.close();
  }

}
